import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import Table from '../common/Table';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Input from '../common/Input';
import api from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import '../../styles/admin.css';

const PlanList = () => {
  const [plans, setPlans]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/plans'); // FIX: .php
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Failed to load plans:', error);
      setError(error.response?.data?.error || 'Failed to load plans');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    try {
      await api.delete(`/admin/plans/${id}`); // FIX: .php
      loadPlans();
    } catch (error) {
      console.error('Failed to delete plan:', error);
      alert(error.response?.data?.error || 'Failed to delete plan');
    }
  };

  const handleSave = async (planData) => {
    try {
      if (editingPlan) {
        await api.put(`/admin/plans/${editingPlan.id}`, planData); // FIX: .php
      } else {
        await api.post('/admin/plans', planData); // FIX: .php
      }
      setShowModal(false);
      setEditingPlan(null);
      loadPlans();
    } catch (error) {
      console.error('Failed to save plan:', error);
      alert(error.response?.data?.error || 'Failed to save plan');
    }
  };

  const columns = [
    { key: 'plan_name', label: 'Plan Name' },
    { key: 'plan_code', label: 'Code' },
    {
      key: 'price_monthly',
      label: 'Monthly Price',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'price_yearly',
      label: 'Yearly Price',
      render: (value) => value ? formatCurrency(value) : '-'
    },
    { key: 'max_users',    label: 'Max Users' },
    { key: 'max_products', label: 'Max Products' },
    {
      key: 'is_active',
      label: 'Status',
      render: (value) => (
        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => { setEditingPlan(row); setShowModal(true); }}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => handleDelete(row.id)}
          >
            Delete
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="plan-list">
      <div className="page-header">
        <h1>Subscription Plans</h1>
        <Button
          variant="primary"
          onClick={() => { setEditingPlan(null); setShowModal(true); }}
        >
          ➕ Add Plan
        </Button>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <Button size="sm" variant="secondary" onClick={loadPlans}>Retry</Button>
        </div>
      )}

      <Card>
        <Table
          columns={columns}
          data={plans}
          loading={loading}
          emptyMessage="No plans found. Create your first plan!"
        />
      </Card>

      <PlanModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingPlan(null); }}
        plan={editingPlan}
        onSave={handleSave}
      />
    </div>
  );
};

const PlanModal = ({ isOpen, onClose, plan, onSave }) => {
  const defaultFeatures = JSON.stringify({
    pos: true, inventory: true, reports: 'basic',
    customers: true, debts: false, suppliers: false, api_access: false
  }, null, 2);

  const [formData, setFormData] = useState({
    plan_name:     '',
    plan_code:     '',
    price_monthly: '',
    price_yearly:  '',
    max_users:     '',
    max_products:  '',
    features:      defaultFeatures,
    is_active:     true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plan) {
      setFormData({
        plan_name:     plan.plan_name     || '',
        plan_code:     plan.plan_code     || '',
        price_monthly: plan.price_monthly || '',
        price_yearly:  plan.price_yearly  || '',
        max_users:     plan.max_users     || '',
        max_products:  plan.max_products  || '',
        features:      plan.features
          ? (typeof plan.features === 'string'
              ? plan.features
              : JSON.stringify(plan.features, null, 2))
          : defaultFeatures,
        is_active: plan.is_active ?? true
      });
    } else {
      setFormData({
        plan_name: '', plan_code: '', price_monthly: '',
        price_yearly: '', max_users: '', max_products: '',
        features: defaultFeatures, is_active: true
      });
    }
  }, [plan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let features = {};
      try {
        features = JSON.parse(formData.features);
      } catch {
        alert('Invalid JSON in features field');
        setSaving(false);
        return;
      }

      await onSave({
        plan_name:     formData.plan_name.trim(),
        plan_code:     formData.plan_code.trim().toLowerCase().replace(/\s+/g, '_'),
        price_monthly: parseFloat(formData.price_monthly) || 0,
        price_yearly:  formData.price_yearly ? parseFloat(formData.price_yearly) : null,
        max_users:     parseInt(formData.max_users)    || 1,
        max_products:  parseInt(formData.max_products) || 0,
        features:      JSON.stringify(features),
        is_active:     formData.is_active ? 1 : 0
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={plan ? 'Edit Plan' : 'New Plan'} size="lg">
      <form onSubmit={handleSubmit} className="plan-form">
        <div className="form-row">
          <Input label="Plan Name" name="plan_name" value={formData.plan_name} onChange={handleChange} required />
          <Input label="Plan Code" name="plan_code" value={formData.plan_code} onChange={handleChange} required hint="e.g. starter, pro" />
        </div>
        <div className="form-row">
          <Input label="Monthly Price (KES)" type="number" name="price_monthly" value={formData.price_monthly} onChange={handleChange} required min="0" step="100" />
          <Input label="Yearly Price (KES)"  type="number" name="price_yearly"  value={formData.price_yearly}  onChange={handleChange} min="0" step="100" />
        </div>
        <div className="form-row">
          <Input label="Max Users"    type="number" name="max_users"    value={formData.max_users}    onChange={handleChange} required min="1" />
          <Input label="Max Products" type="number" name="max_products" value={formData.max_products} onChange={handleChange} required min="0" />
        </div>
        <div className="form-group">
          <label>Features (JSON)</label>
          <textarea
            name="features"
            value={formData.features}
            onChange={handleChange}
            rows="8"
            className="code-editor"
          />
          <small className="hint">Enter features as a JSON object.</small>
        </div>
        <div className="form-group checkbox">
          <label>
            <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} />
            Active (available for new subscriptions)
          </label>
        </div>
        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={saving}>
            {plan ? 'Update Plan' : 'Create Plan'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default PlanList;